<template>
  <div :class="classes">
    <div class="package-import-v3">
      <label class="file-picker">
        <icon type="packageImportV3" size="1.2"/>
        <span>{{ file ? file.name : $t('packageImportV3ChooseFile') }}</span>
        <input
            ref="fileInput"
            type="file"
            accept=".zip,application/zip"
            :disabled="submitting"
            @change="selectFile"
        >
      </label>
      <button v-if="file && !submitting" type="button" class="remove-file" @click="clearFile">
        {{ $t('removeFile') }}
      </button>

      <div v-if="file" class="file-details mt-2">
        {{ formatBytes(file.size) }}
      </div>

      <div v-if="submitting || job" class="progress-panel mt-4">
        <div class="d-flex justify-content-between">
          <strong>{{ stageLabel }}</strong>
          <span>{{ Math.round(progress * 100) }}%</span>
        </div>
        <div class="progress mt-2">
          <div class="progress-bar" role="progressbar" :style="{width: (progress * 100) + '%'}"/>
        </div>
        <div class="details mt-2">
          <span v-if="job && job.counts && job.counts.total">
            {{ job.counts.processed }} / {{ job.counts.total }}
          </span>
          <span v-if="job && job.counts && job.counts.instances !== undefined" class="ml-3">
            {{ $t('packageImportV3Counts', job.counts) }}
          </span>
          <span v-if="elapsedSeconds" class="ml-3">{{ formatElapsed(elapsedSeconds) }}</span>
        </div>
      </div>

      <div v-if="error" class="form-error mt-3">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import {v4 as uuidv4} from 'uuid';
import Icon from '@/components/Icon';

const CHUNK_BYTES = 50 * 1024 * 1024;
const MAX_FILE_BYTES = 20 * 1024 * 1024 * 1024;

export default {
  name: 'PackageImportV3Upload',
  components: {Icon},
  props: {
    organizationId: {type: String, default: null},
    teamId: {type: String, default: null},
    assetName: {type: String, default: ''},
    assetDescription: {type: String, default: ''},
    classes: {type: String, default: 'col-12 col-md-8 col-lg-6 mt-1'},
  },
  data() {
    return {
      file: null,
      clientRequestId: null,
      job: null,
      progress: 0,
      uploadProgress: 0,
      error: '',
      submitting: false,
      pollTimer: null,
      startedAt: null,
      elapsedSeconds: 0,
      elapsedTimer: null,
    };
  },
  computed: {
    stageLabel() {
      if (!this.job || this.job.status === 'uploading') return this.$t('packageImportV3Uploading');
      if (this.job.status === 'queued') return this.$t('packageImportV3Queued');
      if (this.job.status === 'completed') return this.$t('packageImportV3Completed');
      if (this.job.status === 'failed') return this.$t('packageImportV3Failed');
      return this.$t(`packageImportV3Stage_${this.job.stage || 'validating'}`);
    },
  },
  beforeDestroy() {
    this.stopTimers();
  },
  methods: {
    selectFile(event) {
      const file = event.target.files && event.target.files[0];
      this.error = '';
      if (!file) return this.clearFile();
      if (!file.name.toLowerCase().endsWith('.zip')) {
        this.error = this.$t('packageImportV3ZipOnly');
        return this.clearFile(false);
      }
      if (file.size > MAX_FILE_BYTES) {
        this.error = this.$t('packageImportV3TooLarge');
        return this.clearFile(false);
      }
      this.file = file;
      this.clientRequestId = uuidv4();
      this.job = null;
      this.progress = 0;
      this.$emit('filesInQueue', [file]);
      this.$emit('data', file);
      this.$emit('enableSaving', {value: true});
    },
    clearFile(clearError = true) {
      if (clearError) this.error = '';
      this.file = null;
      this.clientRequestId = null;
      this.job = null;
      this.progress = 0;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      this.$emit('queueEmpty');
      this.$emit('enableSaving', {value: false});
    },
    async runtimeConfig() {
      const existing = this.$store.getters.getConfig;
      if (existing && existing.sfx) return existing;
      return fetch(process.env.VUE_APP_CONFIG).then(response => response.json());
    },
    async endpoint() {
      const runtime = await this.runtimeConfig();
      const webapp = runtime.sfx.project.webapp;
      const importPath = '/package-import-v3';
      return `${webapp.apiHost.replace(/\/$/, '')}/${importPath.replace(/^\//, '').replace(/\/$/, '')}`;
    },
    async api(path, options = {}) {
      const base = await this.endpoint();
      const response = await fetch(`${base}/api/v1${path}`, {
        ...options,
        headers: {
          Authorization: `Bearer ${this.$store.getters.getAuthToken}`,
          ...(options.headers || {}),
        },
      });
      const data = response.status === 204 ? null : await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data && data.error ? data.error : {};
        const error = new Error(detail.message || `${response.status} ${response.statusText}`);
        error.code = detail.code;
        throw error;
      }
      return data;
    },
    uploadChunk(url, chunk, uploadedBefore) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.setRequestHeader('Authorization', `Bearer ${this.$store.getters.getAuthToken}`);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.upload.onprogress = event => {
          if (event.lengthComputable) {
            this.uploadProgress = Math.min(1, (uploadedBefore + event.loaded) / this.file.size);
            this.progress = this.uploadProgress * 0.90;
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) return resolve();
          let message = `${xhr.status} ${xhr.statusText}`;
          let code;
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.error && data.error.message) message = data.error.message;
            if (data.error && data.error.code) code = data.error.code;
          } catch (ignored) {
            // Keep the HTTP status text when the proxy did not return JSON.
          }
          const error = new Error(message);
          error.code = code;
          reject(error);
        };
        xhr.onerror = () => reject(new Error(this.$t('packageImportV3NetworkError')));
        xhr.send(chunk);
      });
    },
    async uploadChunkWithRetry(url, chunk, uploadedBefore) {
      let lastError;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await this.uploadChunk(url, chunk, uploadedBefore);
          return;
        } catch (error) {
          lastError = error;
          if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, attempt)));
        }
      }
      throw lastError;
    },
    async resumeUpload() {
      if (!this.file || this.submitting) return;
      this.submitting = true;
      this.error = '';
      this.startedAt = Date.now();
      this.elapsedSeconds = 0;
      this.elapsedTimer = setInterval(() => {
        this.elapsedSeconds = Math.floor((Date.now() - this.startedAt) / 1000);
      }, 1000);
      try {
        const totalChunks = Math.ceil(this.file.size / CHUNK_BYTES);
        this.job = await this.api('/imports', {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            clientRequestId: this.clientRequestId,
            name: this.assetName,
            description: this.assetDescription,
            organizationId: this.organizationId,
            teamId: this.teamId || undefined,
            fileName: this.file.name,
            fileSize: this.file.size,
            totalChunks,
          }),
        });
        if (this.job.status === 'uploading') {
          const base = await this.endpoint();
          for (let index = 0; index < totalChunks; index++) {
            const start = index * CHUNK_BYTES;
            const chunk = this.file.slice(start, Math.min(this.file.size, start + CHUNK_BYTES));
            await this.uploadChunkWithRetry(`${base}/api/v1/imports/${this.job.id}/chunks/${index}`, chunk, start);
          }
          this.progress = 0.90;
          this.job = await this.api(`/imports/${this.job.id}/complete`, {method: 'POST'});
        }
        await this.pollStatus();
      } catch (error) {
        this.error = this.formatError(error);
        this.submitting = false;
        this.stopTimers();
        this.$emit('failed', error);
      }
    },
    async pollStatus() {
      try {
        this.job = await this.api(`/imports/${this.job.id}`);
        this.progress = 0.90 + (Number(this.job.progress || 0) * 0.10);
        if (this.job.status === 'completed') {
          this.progress = 1;
          this.submitting = false;
          this.stopTimers();
          this.$emit('finished', this.job.result.assemblyAssetId, true);
          return;
        }
        if (this.job.status === 'failed') {
          throw new Error(this.job.error ? this.job.error.message : this.$t('packageImportV3Failed'));
        }
        this.pollTimer = setTimeout(() => this.pollStatus(), 1000);
      } catch (error) {
        this.error = this.formatError(error);
        this.submitting = false;
        this.stopTimers();
        this.$emit('failed', error);
      }
    },
    stopTimers() {
      if (this.pollTimer) clearTimeout(this.pollTimer);
      if (this.elapsedTimer) clearInterval(this.elapsedTimer);
      this.pollTimer = null;
      this.elapsedTimer = null;
    },
    formatBytes(bytes) {
      if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatElapsed(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainder = seconds % 60;
      return `${minutes}:${String(remainder).padStart(2, '0')}`;
    },
    formatError(error) {
      return error.code ? `${error.code}: ${error.message}` : error.message;
    },
  },
};
</script>

<style lang="scss" scoped>
.package-import-v3 {
  .file-picker {
    align-items: center;
    border: 1px dashed $tile-active-border-color;
    cursor: pointer;
    display: flex;
    gap: 0.75rem;
    min-height: 5rem;
    padding: 1rem;

    input { display: none; }
  }

  .remove-file {
    background: transparent;
    border: 0;
    color: inherit;
    margin-top: 0.5rem;
    padding: 0;
    text-decoration: underline;
  }

  .progress-bar { background-color: $tile-active-border-color; }
  .details { opacity: 0.75; }
}
</style>
